import React, { useEffect, useCallback } from 'react';
import { X } from 'lucide-react';
import './ProCompareModal.css';
import { getStripeProPaymentUrl, isStripeBillingEnabled } from '../config/stripe';
import { FEATURE_LIMITS } from '../utils/membership';
import { SHOW_QUIZ_FEATURES } from '../config/features';

function buildCompareRows() {
  const f = FEATURE_LIMITS;
  const rows = [
    {
      feature: 'AI chats & responses',
      free: `${f.free.aiResponses}/day`,
      pro: 'Unlimited',
    },
    {
      feature: 'PDF & file uploads',
      free: `${f.free.documentUploads}/day`,
      pro:
        f.pro.documentUploads === 'unlimited'
          ? 'Unlimited'
          : `${f.pro.documentUploads}/day`,
    },
    {
      feature: 'YouTube → notes',
      free: `${f.free.youtubeVideos}/day`,
      pro: 'Unlimited',
    },
    {
      feature: 'Flashcards',
      free: 'Basic',
      pro: 'Advanced',
    },
  ];
  if (SHOW_QUIZ_FEATURES) {
    rows.splice(3, 0, {
      feature: 'Quizzes & grading',
      free: 'Limited',
      pro: 'Full access',
    });
  }
  return rows;
}

const ProCompareModal = ({ isOpen, onClose, user }) => {
  const rows = buildCompareRows();

  const handleUpgrade = useCallback(() => {
    const base = getStripeProPaymentUrl();
    if (!base) return;
    const sep = base.includes('?') ? '&' : '?';
    const url = user?.uid
      ? `${base}${sep}client_reference_id=${encodeURIComponent(user.uid)}`
      : base;
    window.open(url, '_blank', 'noopener,noreferrer');
    onClose();
  }, [user?.uid, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="pro-compare-overlay" onClick={onClose} role="presentation">
      <div
        className="pro-compare-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="pro-compare-title"
      >
        <button type="button" className="pro-compare-close" onClick={onClose} aria-label="Close">
          <X size={22} />
        </button>

        <h2 id="pro-compare-title" className="pro-compare-title">
          Upgrade your account
        </h2>
        <p className="pro-compare-subtitle">Compare Free and Pro — unlock more study power.</p>

        <div className="pro-compare-table-wrap">
          <table className="pro-compare-table">
            <thead>
              <tr>
                <th scope="col">Included</th>
                <th scope="col">Free</th>
                <th scope="col" className="pro-compare-col-pro">
                  Pro
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.feature}>
                  <td className="pro-compare-feature">{row.feature}</td>
                  <td className="pro-compare-free">{row.free}</td>
                  <td className="pro-compare-pro">{row.pro}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <button type="button" className="pro-compare-upgrade-btn" onClick={handleUpgrade}>
          Upgrade to Pro
        </button>
        {!isStripeBillingEnabled() && (
          <p className="pro-compare-footnote">
            Opens secure Stripe checkout. Enable billing in your environment when ready.
          </p>
        )}
      </div>
    </div>
  );
};

export default ProCompareModal;
